
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Clock, Plus, X } from 'lucide-react';
import { TagInput } from '@/components/ui/tag-input';
import { getTaskCategoryOptions } from '@/services/leads/taskService';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';

interface TaskFormProps {
  newTaskTitle: string;
  setNewTaskTitle: (title: string) => void;
  newTaskDescription: string;
  setNewTaskDescription: (description: string) => void;
  newTaskDueDate: string;
  setNewTaskDueDate: (date: string) => void;
  newTaskPriority: string;
  setNewTaskPriority: (priority: string) => void;
  newTaskAssignedTo: string;
  setNewTaskAssignedTo: (assignedTo: string) => void;
  priorityOptions: { value: string; label: string }[];
  newTaskCategories?: string[];
  setNewTaskCategories?: (categories: string[]) => void;
  newTaskReminderTimes?: string[];
  setNewTaskReminderTimes?: (reminderTimes: string[]) => void;
}

const TaskForm: React.FC<TaskFormProps> = ({
  newTaskTitle,
  setNewTaskTitle,
  newTaskDescription,
  setNewTaskDescription,
  newTaskDueDate,
  setNewTaskDueDate,
  newTaskPriority,
  setNewTaskPriority,
  newTaskAssignedTo,
  setNewTaskAssignedTo,
  priorityOptions,
  newTaskCategories = [],
  setNewTaskCategories = () => {},
  newTaskReminderTimes = [],
  setNewTaskReminderTimes = () => {},
}) => {
  const { data: companyMembers, isLoading: isLoadingMembers } = useCompanyMembers();
  const { user } = useAuth();
  const categoryOptions = getTaskCategoryOptions();
  const [customReminderMinutes, setCustomReminderMinutes] = useState("");

  // Set current user as default assigned user when form is rendered and no value is set
  React.useEffect(() => {
    if (user && !newTaskAssignedTo) {
      setNewTaskAssignedTo(user.id);
    }
  }, [user, newTaskAssignedTo, setNewTaskAssignedTo]);

  const predefinedReminders = [
    { value: "15", label: "15 minutes before" },
    { value: "30", label: "30 minutes before" },
    { value: "60", label: "1 hour before" },
    { value: "120", label: "2 hours before" },
    { value: "1440", label: "1 day before" },
  ];

  const addReminderTime = (minutes: string) => {
    if (!minutes || newTaskReminderTimes.includes(minutes)) return;
    
    const updatedReminders = [...newTaskReminderTimes, minutes];
    setNewTaskReminderTimes(updatedReminders);
    setCustomReminderMinutes("");
  };

  const removeReminderTime = (minutes: string) => {
    const updatedReminders = newTaskReminderTimes.filter(m => m !== minutes);
    setNewTaskReminderTimes(updatedReminders);
  };

  const formatReminderTime = (minutes: string) => {
    const mins = parseInt(minutes);
    if (mins < 60) {
      return `${mins} minute${mins !== 1 ? 's' : ''} before`;
    } else if (mins < 1440) {
      const hours = mins / 60;
      return `${hours} hour${hours !== 1 ? 's' : ''} before`;
    } else {
      const days = mins / 1440;
      return `${days} day${days !== 1 ? 's' : ''} before`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Basic Information Section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="title" className="text-sm font-semibold text-foreground">
            Title <span className="text-destructive">*</span>
          </label>
          <Input
            id="title"
            placeholder="Enter task title"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            className="h-11"
          />
        </div>
        
        <div className="space-y-2">
          <label htmlFor="description" className="text-sm font-semibold text-foreground">
            Description
          </label>
          <RichTextEditor
            value={newTaskDescription}
            onChange={setNewTaskDescription}
            placeholder="Enter task description"
            className="min-h-[100px]"
          />
        </div>
        
        <div className="space-y-2">
          <label htmlFor="categories" className="text-sm font-semibold text-foreground">
            Categories
          </label>
          <TagInput
            selected={newTaskCategories}
            options={categoryOptions}
            onChange={setNewTaskCategories}
            placeholder="Add categories..."
            creatable={true}
          />
        </div>
      </div>

      {/* Separator */}
      <div className="border-t" />

      {/* Task Details Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Task Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="dueDate" className="text-sm font-semibold text-foreground">
              Due Date <span className="text-destructive">*</span>
            </label>
            <Input
              id="dueDate"
              type="datetime-local"
              value={newTaskDueDate}
              onChange={(e) => setNewTaskDueDate(e.target.value)}
              className="h-11"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="priority" className="text-sm font-semibold text-foreground">
              Priority <span className="text-destructive">*</span>
            </label>
            <Select value={newTaskPriority} onValueChange={setNewTaskPriority}>
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                {priorityOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="space-y-2">
          <label htmlFor="assignedTo" className="text-sm font-semibold text-foreground">
            Assign To <span className="text-destructive">*</span>
          </label>
          {isLoadingMembers ? (
            <div className="flex items-center space-x-2 h-11 px-3 border rounded-md bg-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading team members...</span>
            </div>
          ) : (
            <Select value={newTaskAssignedTo} onValueChange={setNewTaskAssignedTo}>
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Select team member">
                  {newTaskAssignedTo && companyMembers?.find(member => member.id === newTaskAssignedTo) && (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={companyMembers.find(member => member.id === newTaskAssignedTo)?.avatar_url} />
                        <AvatarFallback className="text-xs">
                          {companyMembers.find(member => member.id === newTaskAssignedTo)?.first_name?.[0]}
                          {companyMembers.find(member => member.id === newTaskAssignedTo)?.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">
                        {companyMembers.find(member => member.id === newTaskAssignedTo)?.first_name}{' '}
                        {companyMembers.find(member => member.id === newTaskAssignedTo)?.last_name}
                      </span>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                {companyMembers?.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.avatar_url} />
                        <AvatarFallback className="text-xs">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">
                        {member.first_name} {member.last_name}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Separator */}
      <div className="border-t" />

      {/* Reminders Section */}
      <div className="space-y-4">
        <Accordion type="single" collapsible className="w-full border rounded-lg">
          <AccordionItem value="reminders" className="border-none">
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50 rounded-t-lg">
              <div className="flex items-center">
                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Reminders</span>
                {newTaskReminderTimes.length > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-2 text-xs">
                    {newTaskReminderTimes.length}
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4 pt-2">
                {/* Active Reminders */}
                {newTaskReminderTimes.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Active reminders</label>
                    <div className="flex flex-wrap gap-2">
                      {newTaskReminderTimes.map((minutes) => (
                        <Badge 
                          key={minutes} 
                          variant="secondary" 
                          className="px-3 py-1.5 text-xs font-medium"
                        >
                          {formatReminderTime(minutes)}
                          <X 
                            className="h-3 w-3 ml-2 cursor-pointer hover:text-destructive transition-colors" 
                            onClick={() => removeReminderTime(minutes)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Predefined Reminders */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Quick add</label>
                  <div className="grid grid-cols-2 gap-2">
                    {predefinedReminders.map((reminder) => (
                      <Button 
                        key={reminder.value} 
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => addReminderTime(reminder.value)}
                        className="h-9 justify-start text-xs font-normal"
                        disabled={newTaskReminderTimes.includes(reminder.value)}
                      >
                        <Plus className="h-3 w-3 mr-1.5" />
                        {reminder.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Custom Reminder */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Custom reminder</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="1"
                      placeholder="Minutes before"
                      value={customReminderMinutes}
                      onChange={(e) => setCustomReminderMinutes(e.target.value)}
                      className="flex-1 h-9 text-sm"
                    />
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => addReminderTime(customReminderMinutes)}
                      disabled={!customReminderMinutes || parseInt(customReminderMinutes) <= 0}
                      className="h-9 px-4"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

export default TaskForm;
